import { useIntl } from 'react-intl';
import { Paper } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import MDBox from 'components/MDBox';
import { useDeleteAssistantMutation, useGetAssistantsQuery } from 'services/assistantApi';
import { GridCards } from 'components/Cards/GridCards';
import IndeterminateLoading from 'components/Loading/IndeterminateLoading';
import { useNotification } from 'hook/useNotification';
import AssistantDetailsDrawer from 'pages/assistants/components/assistantDetailsDrawer';
import AssistantPlayDialog from 'pages/assistants/components/assistantPlayDialog';
import { useAuth } from 'hook/useAuth';
import { RoleEnum } from 'pages/authentication/sign-in';
import { isAuthorized } from 'utils/userUtils';

export function Assistants() {
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const { formatMessage: msg } = useIntl();

  const [assistantDetailDrawerOpen, setAssistantDetailDrawerOpen] = useState(false);
  const [playAssistantDialogOpen, setPlayAssistantDialogOpen] = useState(false);
  const [selectedAssistant, setSelectedAssistant] = useState(null);

  const { currentUser } = useAuth();
  const { user } = currentUser;

  const [deleteAssistant] = useDeleteAssistantMutation();

  const {
    data: assistants,
    error: assistantsError,
    isLoading: assistantsLoading
  } = useGetAssistantsQuery();

  const handleDelete = (id) => {
    deleteAssistant(id)
      .unwrap()
      .then(() => {
        addNotification({
          type: 'success',
          title: msg({ id: 'notification.success.title' }),
          message: msg({ id: 'page.assistants.delete.success' })
        });
      })
      .catch((error) => {
        console.error(error);
        addNotification({
          type: 'error',
          title: msg({ id: 'notification.error.title' }),
          message: msg({ id: 'page.assistants.delete.error' })
        });
      });
  };

  const handleSelect = (id) => {
    navigate(`/portal/assistants/builder/${id}`);
  };

  if (assistantsLoading) {
    return (
      <IndeterminateLoading
        sx={{ width: '100%', color: '#125688' }}
        message={msg({ id: 'loading.configuration' })}
      />
    );
  }

  if (assistantsError)
    try {
      addNotification({
        type: 'error',
        title: msg({ id: 'notification.warning.title' }),
        message: msg({ id: 'page.assistant.fetch.error' })
      });
    } catch {
      // Ignore exception
    }

  const assistantItems = assistants
    ?.map((assistant) => ({
      id: assistant.id,
      label: assistant.label,
      icon: assistant.icon,
      color: assistant.color,
      description: assistant.description
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const handlePlayAssistant = (assistantId) => {
    setSelectedAssistant(assistants.find((assistant) => assistant.id === assistantId));
    setPlayAssistantDialogOpen(true);
  };

  const handleEdit = (assistantId) => {
    setSelectedAssistant(assistants.find((assistant) => assistant.id === assistantId));
    setAssistantDetailDrawerOpen(true);
  };

  const handleCreate = () => {
    console.debug('create');
    setSelectedAssistant(null);
    setAssistantDetailDrawerOpen(true);
  };

  return (
    <>
      <MDBox id="assistantsContainer" sx={{ display: 'flex', flex: 1 }}>
        <Paper
          elevation={2}
          sx={{
            flex: 1,
            borderRadius: '10px',
            p: 2,
            mb: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <GridCards
            defaultIcon="assistant_rounded"
            items={assistantItems}
            onCreate={handleCreate}
            onDelete={handleDelete}
            onUpdate={handleEdit}
            onSelect={handleSelect}
            onPlay={handlePlayAssistant}
            canEdit={isAuthorized(user, RoleEnum.ADMIN)}
          />
        </Paper>
      </MDBox>
      {selectedAssistant && (
        <AssistantPlayDialog
          dialogOpen={playAssistantDialogOpen}
          setDialogOpen={setPlayAssistantDialogOpen}
          assistantCode={selectedAssistant?.code}
        />
      )}
      <AssistantDetailsDrawer
        drawerOpen={assistantDetailDrawerOpen}
        setDrawerOpen={setAssistantDetailDrawerOpen}
        assistant={selectedAssistant}
      />
    </>
  );
}
